import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { DepositLaunchModal } from '@/components/DepositLaunchModal'
import { InventoryLaunchModal } from '@/components/InventoryLaunchModal'
import InviteAnnouncementModal from '@/components/InviteAnnouncementModal'
import { INVENTORY_LAUNCH_ANNOUNCEMENT } from '@/lib/inventoryLaunch'
import { isEventOpen } from '@/lib/referralEvent'
import { getActiveReferralEvent, type ReferralEvent } from '@/supabase/referral/repository/referralEventRepository'

type GateState = { status: 'loading' } | { status: 'done'; event: ReferralEvent | null }

const sameEvent = (prev: GateState, next: ReferralEvent | null) =>
  prev.status === 'done' &&
  prev.event?.id === next?.id &&
  prev.event?.is_full === next?.is_full &&
  prev.event?.end_at === next?.end_at &&
  prev.event?.reward_multiplier === next?.reward_multiplier &&
  prev.event?.max_referees === next?.max_referees

/**
 * 로그인 공지 우선순위 (docs/superpowers/plans/2026-10-26-referral-event-yearend.md §5).
 * 진행 중인 초대 이벤트(기간 한정)가 있으면 연말 공지만, 없으면 기존 출시 공지(Deposit/Inventory).
 * 두 종류를 동시에 마운트하지 않는다 — 출시 공지는 Radix Dialog라 body pointer-events를 잠가 겹치면 클릭 불능.
 * state를 App이 아닌 이 컴포넌트에 둬 재조회가 라우트 트리를 재렌더하지 않게 한다.
 */
export function LaunchNoticeGate() {
  const { user } = useAuth()
  const userId = user?.id ?? null
  const { pathname } = useLocation()
  const [state, setState] = useState<GateState>({ status: 'loading' })

  // 이벤트 시작·마감·종료를 오래 열린 세션에도 반영: 경로 이동·탭 복귀마다 재조회
  useEffect(() => {
    if (!userId) return
    let cancelled = false
    const load = () => {
      getActiveReferralEvent()
        .then((event) => {
          if (!cancelled) setState((prev) => (sameEvent(prev, event) ? prev : { status: 'done', event }))
        })
        .catch((error) => {
          console.error('[LaunchNoticeGate] 이벤트 조회 오류:', error)
          // 조회 실패 = 기존 출시 공지(현행 동작). 마감·종료 뒤 실패에 이전 판정으로 이벤트를 계속 광고하지 않게 매번 전환.
          if (!cancelled) setState((prev) => (sameEvent(prev, null) ? prev : { status: 'done', event: null }))
        })
    }
    load()
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [userId, pathname])

  if (state.status === 'loading') return null
  if (isEventOpen(state.event)) return <InviteAnnouncementModal event={state.event} />
  return (
    <>
      <DepositLaunchModal /> {/* 입금매칭 출시 안내 (1회성) */}
      {INVENTORY_LAUNCH_ANNOUNCEMENT && <InventoryLaunchModal />} {/* 재고관리 출시 안내 (1회성) */}
    </>
  )
}
